import { Check, ChevronDown, Search, SearchX } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { useNaCamadaDeCima } from '@/lib/camada';
import { obrigatoriaNaMesa, useOpcaoUnica } from '@/lib/escolha-unica';

import type { OpcaoDeSelecao } from './Campo';
import { EtiquetaDaOpcao } from './EtiquetaDaOpcao';
import { LeitorDoAdesivoDoCampo } from './LeitorDoAdesivo';
import {
  ALTURA_MAXIMA_DA_LISTA,
  FECHADO,
  aoAbrir,
  eLetra,
  filtrarOpcoes,
  itensDoSeletor,
  lugarDaLista,
  saltarPorLetras,
  temBusca,
  teclar,
  textoInteiro,
  type EstadoDoSeletor,
  type LugarDaLista,
} from './lista-de-escolha';

/** Quanto tempo as letras digitadas somam antes de recomeçar ("tr" e não "t" e depois "r"). */
const PAUSA_DAS_LETRAS_MS = 500;

/** A largura mínima da lista (p.31: popover de 200 a 360 px). */
const LARGURA_MINIMA = 200;
const LARGURA_COM_VISUAL = 320;
/** Com etiquetas (código interno, placa), o teto do popover: o código e as etiquetas cabem numa linha. */
const LARGURA_COM_ETIQUETAS = 360;

/**
 * O SELETOR DO CAMPO (v2.1, p.23 e p.31) — o gatilho com a cara do campo e a
 * lista colada a ele.
 *
 * Substitui o `<select>` nativo (pedido do dono, 20/10/2026): a lista nativa
 * não mostra foto nem segunda linha, e cada sistema a desenha de um jeito.
 *
 * - O GATILHO é o `controle` de 40 px e raio 12, com a seta `chevron-down`; o
 *   foco é o do campo (borda laranja com halo), nunca removido.
 * - A LISTA é o popover da p.31: colada ao campo a 8 px, raio do card, sombra
 *   elevada, invertendo para cima quando embaixo não cabe, e rolando por
 *   dentro a partir de 320 px.
 * - Com mais de sete opções ela abre com BUSCA e o foco vai para ela.
 * - Mora na CAMADA DE CIMA (`popover`, como o toast em `lib/camada`): dentro
 *   da janela e da gaveta ela fica acima do fundo escurecido e não é cortada
 *   pelo corpo que rola. Ela é filha do campo no DOM, então continua dentro do
 *   `<dialog>` — nada dela fica inerte atrás da janela.
 *
 * A decisão de cada tecla está em `lista-de-escolha.ts`, provada sem navegador.
 */
export function ListaDeEscolha({
  id,
  rotulo,
  valor,
  aoMudar,
  opcoes,
  rotuloVazio,
  aplicado = false,
  desabilitado = false,
  idDoErro,
  aoSair,
  opcaoUnica: regraDaUnica,
  adesivo,
}: {
  readonly id: string;
  readonly rotulo: string;
  readonly valor: string;
  readonly aoMudar: (valor: string) => void;
  readonly opcoes: readonly OpcaoDeSelecao[];
  readonly rotuloVazio?: string | undefined;
  readonly aplicado?: boolean;
  readonly desabilitado?: boolean;
  /** Com erro, o campo aponta para a mensagem (`aria-describedby`). */
  readonly idDoErro?: string | undefined;
  /**
   * O foco saiu do campo e da lista. É onde o formulário valida (p.23: "ao
   * sair do campo"), como o `onBlur` do `<select>` que este campo substitui.
   */
  readonly aoSair?: () => void;
  /**
   * O CAMPO DE OPÇÃO ÚNICA (`lib/escolha-unica.ts`): obrigatório e com uma
   * opção só, ele não abre lista — mostra a opção, travado, e a manda no
   * envio. O padrão lê a opção vazia ("Escolha…" é convite; "Todas" e "Sem…"
   * são resposta); `false` desliga, `true` liga mesmo com outra opção vazia.
   */
  readonly opcaoUnica?: boolean;
  /**
   * O CAMPO DA MÁQUINA LÊ O ADESIVO (decisão 64, pedido do dono de
   * 31/10/2026): o botão `qr-code` ao lado abre a câmera (ou lê de uma foto),
   * e o leitor de código USB que digita o conteúdo do QR e aperta Enter
   * escolhe direto. As opções trazem o adesivo (`opcaoDaMaquina`).
   * `porQueFora` diz por que uma máquina que existe pode não estar nesta
   * lista ("a folha de turno é só de máquina com horímetro").
   */
  readonly adesivo?: boolean | { readonly porQueFora?: string };
}) {
  const base = useId();
  const obrigatoria = regraDaUnica ?? obrigatoriaNaMesa({ aplicado, rotuloVazio });
  const unica = useOpcaoUnica({ opcoes: opcoes.map((o) => o.valor), obrigatoria, valor, aoMudar });
  const travada = unica !== null;
  const idDaLista = `${id}-lista`;
  const idDaOpcao = (i: number) => `${base}-opcao-${String(i)}`;

  const caixa = useRef<HTMLDivElement>(null);
  const gatilho = useRef<HTMLButtonElement>(null);
  const folha = useRef<HTMLDivElement>(null);
  const lista = useRef<HTMLDivElement>(null);
  const busca = useRef<HTMLInputElement>(null);
  const letras = useRef<{ texto: string; ate: number }>({ texto: '', ate: 0 });

  const [estado, setEstado] = useState<EstadoDoSeletor>(FECHADO);
  const [lugar, setLugar] = useState<LugarDaLista | null>(null);

  const todos = useMemo(() => itensDoSeletor(opcoes, rotuloVazio), [opcoes, rotuloVazio]);
  const comBusca = temBusca(todos.length);
  const itens = useMemo(() => (comBusca ? filtrarOpcoes(todos, estado.termo) : todos), [comBusca, todos, estado.termo]);
  /* Travada, a opção mostrada é a única desde o primeiro desenho — antes de o
     formulário receber o valor, o campo não pisca o "Escolha…". */
  const escolhida = todos.find((o) => o.valor === (travada ? unica : valor)) ?? null;
  const contexto = { itens, valor, busca: comBusca };
  /* Alguma opção tem miniatura? Então TODAS reservam a caixa de 32 px — sem
     isso, o texto anda de lugar de uma linha para a outra. A opção vazia
     ("Escolha a máquina") também, pelo mesmo motivo. */
  const comVisual = todos.some((o) => o.imagem !== undefined || o.icone !== undefined || o.iniciais !== undefined);
  const comEtiquetas = todos.some((o) => (o.etiquetas?.length ?? 0) > 0);

  const fechar = useCallback((devolverFoco: boolean) => {
    setEstado(FECHADO);
    if (devolverFoco) gatilho.current?.focus();
  }, []);

  const escolher = (novo: string) => {
    fechar(true);
    if (novo !== valor) aoMudar(novo);
  };

  /* A lista na camada de cima: entra lá a cada abertura. */
  useNaCamadaDeCima(folha, estado.aberto ? 'aberta' : undefined);

  /* Onde ela assenta: medido do campo, de novo a cada rolagem e mudança de
     tamanho da tela — a lista acompanha o campo em vez de ficar para trás. */
  const medir = useCallback(() => {
    const r = gatilho.current?.getBoundingClientRect();
    if (!r) return;
    const cabecalho = folha.current?.querySelector<HTMLElement>('[data-busca-da-lista]')?.offsetHeight ?? 0;
    const conteudo = lista.current?.scrollHeight ?? ALTURA_MAXIMA_DA_LISTA;
    setLugar(
      lugarDaLista({
        campo: r,
        alturaDesejada: cabecalho + conteudo + 8,
        tela: { largura: window.innerWidth, altura: window.innerHeight },
        /* Com miniatura e segunda linha, a lista pede mais largura que o
           campo estreito: até 320 px, dentro dos 200–360 do popover (p.31). */
        larguraMinima: comEtiquetas ? LARGURA_COM_ETIQUETAS : comVisual ? LARGURA_COM_VISUAL : LARGURA_MINIMA,
      }),
    );
  }, [comVisual, comEtiquetas]);

  useLayoutEffect(() => {
    if (!estado.aberto) {
      setLugar(null);
      return undefined;
    }
    medir();
    let quadro = 0;
    const remedir = (evento: Event) => {
      /* Rolar a PRÓPRIA lista não move o campo. */
      if (evento.target instanceof Node && folha.current?.contains(evento.target)) return;
      cancelAnimationFrame(quadro);
      quadro = requestAnimationFrame(medir);
    };
    window.addEventListener('scroll', remedir, true);
    window.addEventListener('resize', remedir);
    return () => {
      cancelAnimationFrame(quadro);
      window.removeEventListener('scroll', remedir, true);
      window.removeEventListener('resize', remedir);
    };
  }, [estado.aberto, medir]);

  /* Clique fora fecha, sem roubar o foco de onde a pessoa clicou. */
  useEffect(() => {
    if (!estado.aberto) return undefined;
    const fora = (evento: PointerEvent) => {
      if (evento.target instanceof Node && caixa.current?.contains(evento.target)) return;
      fechar(false);
    };
    document.addEventListener('pointerdown', fora);
    return () => {
      document.removeEventListener('pointerdown', fora);
    };
  }, [estado.aberto, fechar]);

  /* Com busca, o foco vai para ela ao abrir — depois de a lista ter lugar:
     antes disso ela está escondida (`visibility: hidden`) e não aceita foco. */
  const posicionada = lugar !== null;
  useEffect(() => {
    if (estado.aberto && comBusca && posicionada) busca.current?.focus();
  }, [estado.aberto, comBusca, posicionada]);

  /* A opção ativa sempre à vista dentro da lista que rola. */
  useEffect(() => {
    if (!estado.aberto || estado.ativo < 0) return;
    const no = document.getElementById(`${base}-opcao-${String(estado.ativo)}`);
    if (no && typeof no.scrollIntoView === 'function') no.scrollIntoView({ block: 'nearest' });
  }, [base, estado.aberto, estado.ativo]);

  const aplicar = (evento: KeyboardEvent<HTMLElement>, origem: 'gatilho' | 'busca') => {
    if (evento.key === 'Tab' && origem === 'busca') {
      /* O Tab sai da lista a partir do CAMPO: para a frente, segue ao
         próximo campo do formulário; para trás, para no próprio campo. */
      gatilho.current?.focus();
      if (evento.shiftKey) evento.preventDefault();
    }
    const resposta = teclar(estado, evento, contexto, origem);
    if (resposta.consumida) {
      evento.preventDefault();
      /* O Esc que fecha a lista não fecha a janela de trás. */
      evento.stopPropagation();
    }
    setEstado(resposta.estado);
    if (resposta.escolher !== undefined && resposta.escolher !== valor) aoMudar(resposta.escolher);
    if (resposta.focar === 'gatilho') gatilho.current?.focus();
    return resposta.consumida;
  };

  const aoTeclarNoGatilho = (evento: KeyboardEvent<HTMLButtonElement>) => {
    if (eLetra(evento)) {
      evento.preventDefault();
      if (comBusca) {
        /* A primeira letra já é o começo da busca. */
        setEstado({ aberto: true, ativo: 0, termo: evento.key });
        return;
      }
      const agora = Date.now();
      const texto = agora < letras.current.ate ? letras.current.texto + evento.key : evento.key;
      letras.current = { texto, ate: agora + PAUSA_DAS_LETRAS_MS };
      const desde = estado.aberto ? estado.ativo : todos.findIndex((o) => o.valor === valor);
      const achada = saltarPorLetras(todos, texto, desde);
      setEstado({ aberto: true, ativo: achada >= 0 ? achada : 0, termo: '' });
      return;
    }
    aplicar(evento, 'gatilho');
  };

  /* O leitor só aparece onde há o que escolher: no campo travado ou desligado
     ele seria botão que não faz nada. */
  const comLeitor = adesivo !== undefined && adesivo !== false && !desabilitado && !travada;

  const visivelDaEscolhida =
    escolhida && (escolhida.imagem !== undefined || escolhida.icone !== undefined || escolhida.iniciais !== undefined) ? (
      <Visual opcao={escolhida} tamanho="p" />
    ) : null;

  return (
    <div
      ref={caixa}
      className={comLeitor ? 'relative grid grid-cols-[minmax(0,1fr)_auto] gap-x-2' : 'relative'}
      onBlur={(evento) => {
        /* O foco saiu do campo e da lista (Tab, clique em outro campo): fecha. */
        const para = evento.relatedTarget;
        if (para instanceof Node && caixa.current?.contains(para)) return;
        if (estado.aberto) fechar(false);
        aoSair?.();
      }}
    >
      <button
        ref={gatilho}
        id={id}
        type="button"
        role="combobox"
        disabled={desabilitado || travada}
        {...(travada ? { 'data-opcao-unica': '' } : {})}
        aria-haspopup="listbox"
        aria-expanded={estado.aberto}
        aria-controls={idDaLista}
        {...(estado.aberto && !comBusca && estado.ativo >= 0 ? { 'aria-activedescendant': idDaOpcao(estado.ativo) } : {})}
        {...(idDoErro ? { 'aria-invalid': true, 'aria-describedby': idDoErro } : {})}
        className={`controle flex w-full cursor-pointer items-center gap-2 text-left disabled:cursor-not-allowed ${aplicado ? 'border-marca bg-marca-fundo' : ''}`}
        onClick={() => {
          if (estado.aberto) fechar(true);
          else setEstado(aoAbrir(contexto));
        }}
        onKeyDown={aoTeclarNoGatilho}
        onKeyUp={(evento) => {
          /* O botão nativo dispara o clique no `keyup` do Espaço: a tecla já
             foi tratada no `keydown`. */
          if (evento.key === ' ') evento.preventDefault();
        }}
      >
        {visivelDaEscolhida}
        {escolhida ? (
          <EscolhidaNoCampo opcao={escolhida} />
        ) : (
          <span className="min-w-0 flex-1 truncate text-tinta-secundaria">Escolha uma opção</span>
        )}
        {travada ? null : (
          <ChevronDown
            aria-hidden="true"
            className={`size-[var(--icone-controle)] shrink-0 text-tinta-secundaria transition-transform duration-[var(--mov-rapido)] ${estado.aberto ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {comLeitor ? (
        <LeitorDoAdesivoDoCampo
          caixa={caixa}
          rotulo={rotulo}
          opcoes={opcoes}
          porQueFora={typeof adesivo === 'object' ? adesivo.porQueFora : undefined}
          aoEscolher={(novo) => {
            escolher(novo);
          }}
          aoRecusar={() => {
            fechar(true);
          }}
        />
      ) : null}

      {estado.aberto ? (
        <div
          ref={folha}
          data-camada-de-cima
          style={
            lugar
              ? {
                  left: lugar.esquerda,
                  width: lugar.largura,
                  maxHeight: lugar.alturaMaxima,
                  /* Aberta para cima, ela cresce a partir da base (p.21). */
                  ...(lugar.acima
                    ? { bottom: lugar.distancia, top: 'auto', transformOrigin: 'bottom' }
                    : { top: lugar.distancia, bottom: 'auto' }),
                }
              : { visibility: 'hidden' }
          }
          /* O clique numa opção não tira o foco do campo antes de valer. */
          onMouseDown={(evento) => {
            if (!(evento.target instanceof HTMLInputElement)) evento.preventDefault();
          }}
          className={`fixed z-[var(--z-dica)] flex flex-col overflow-hidden rounded-card border border-borda-sutil bg-flutuante p-1 shadow-elevado anima-menu`}
        >
          {comBusca ? (
            <div data-busca-da-lista className="relative shrink-0 p-1 pb-2">
              <Search aria-hidden="true" className="pointer-events-none absolute top-1/2 left-4 size-[var(--icone-controle)] -translate-y-[calc(50%+2px)]" />
              <input
                ref={busca}
                type="text"
                role="combobox"
                aria-label={`Buscar em ${rotulo}`}
                aria-autocomplete="list"
                aria-expanded="true"
                aria-controls={idDaLista}
                {...(estado.ativo >= 0 ? { 'aria-activedescendant': idDaOpcao(estado.ativo) } : {})}
                value={estado.termo}
                placeholder="Digite para buscar"
                autoComplete="off"
                spellCheck={false}
                className="controle h-[var(--controle-p)] w-full pl-9"
                onChange={(evento) => {
                  setEstado({ aberto: true, termo: evento.target.value, ativo: 0 });
                }}
                onKeyDown={(evento) => {
                  aplicar(evento, 'busca');
                }}
              />
            </div>
          ) : null}

          <div
            ref={lista}
            id={idDaLista}
            role="listbox"
            aria-label={rotulo}
            className="min-h-0 flex-auto overflow-y-auto overscroll-contain"
          >
            {itens.map((opcao, i) => (
              <Opcao
                key={opcao.valor === '' ? '__vazia' : opcao.valor}
                id={idDaOpcao(i)}
                opcao={opcao}
                ativa={i === estado.ativo}
                escolhida={opcao.valor === valor}
                comVisual={comVisual}
                aoApontar={() => {
                  if (i !== estado.ativo) setEstado({ ...estado, ativo: i });
                }}
                aoEscolher={() => {
                  escolher(opcao.valor);
                }}
              />
            ))}
          </div>

          {itens.length === 0 ? (
            <p role="status" className="txt-apoio flex items-start gap-2 px-3 py-2 text-tinta-secundaria">
              <SearchX aria-hidden="true" className="mt-px size-[var(--icone-controle)] shrink-0" />
              <span>
                {todos.length === 0
                  ? 'Nenhuma opção para escolher ainda.'
                  : `Nenhum resultado para “${estado.termo.trim()}”. Apague a busca ou tente outra palavra.`}
              </span>
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Opcao({
  id,
  opcao,
  ativa,
  escolhida,
  comVisual,
  aoApontar,
  aoEscolher,
}: {
  readonly id: string;
  readonly opcao: OpcaoDeSelecao;
  readonly ativa: boolean;
  readonly escolhida: boolean;
  readonly comVisual: boolean;
  readonly aoApontar: () => void;
  readonly aoEscolher: () => void;
}): ReactNode {
  const etiquetas = opcao.etiquetas ?? [];
  return (
    <div
      id={id}
      role="option"
      aria-selected={escolhida}
      title={textoInteiro(opcao)}
      onMouseMove={aoApontar}
      onClick={aoEscolher}
      className={`txt-corpo flex min-h-10 cursor-pointer items-center gap-3 rounded-controle px-3 py-2 text-tinta ${ativa ? 'bg-rebaixada' : ''}`}
    >
      {comVisual ? <Visual opcao={opcao} tamanho="m" /> : null}
      <span className="flex min-w-0 flex-1 flex-col">
        {etiquetas.length > 0 ? (
          /* O código não corta (pedido do dono, 31/10/2026): ele e as
             etiquetas quebram para a linha de baixo antes de encolher. */
          <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className={`shrink-0 ${escolhida ? 'font-semibold' : ''}`}>{opcao.rotulo}</span>
            {etiquetas.map((e) => (
              <EtiquetaDaOpcao key={`${e.tom}-${e.texto}`} etiqueta={e} />
            ))}
          </span>
        ) : (
          <span className={`truncate ${escolhida ? 'font-semibold' : ''}`}>{opcao.rotulo}</span>
        )}
        {opcao.detalhe ? <span className="txt-linha2 truncate text-tinta-secundaria">{opcao.detalhe}</span> : null}
      </span>
      {/* O marcado é o `check` do vocabulário (p.19), na cor da marca; a
          opção sem marca reserva o lugar para o texto não andar. */}
      <Check
        aria-hidden="true"
        className={`size-[var(--icone-controle)] shrink-0 text-marca ${escolhida ? '' : 'invisible'}`}
      />
    </div>
  );
}

/**
 * A ESCOLHIDA NO CAMPO FECHADO, numa linha de 40 px: o rótulo (o código da
 * máquina), as etiquetas e a segunda linha em 12 px no que sobrar. O código
 * e as etiquetas ficam inteiros; a segunda linha encolhe com reticências e,
 * quando sobram menos de 96 px para ela, sai inteira (quebra para uma linha
 * que o campo não mostra; o vão de 8 px entre as linhas a mantém fora da
 * caixa de 24) em vez de virar um "CA…". O `title` tem o texto inteiro.
 */
function EscolhidaNoCampo({ opcao }: { readonly opcao: OpcaoDeSelecao }) {
  const etiquetas = opcao.etiquetas ?? [];
  if (etiquetas.length === 0 && !opcao.detalhe) {
    return <span className="min-w-0 flex-1 truncate">{opcao.rotulo}</span>;
  }
  return (
    <span
      className="flex h-6 min-w-0 flex-1 flex-wrap content-start items-center gap-x-2 gap-y-2 overflow-hidden"
      title={textoInteiro(opcao)}
    >
      <span className={etiquetas.length > 0 ? 'shrink-0' : 'min-w-0 truncate'}>{opcao.rotulo}</span>
      {etiquetas.map((e) => (
        <EtiquetaDaOpcao key={`${e.tom}-${e.texto}`} etiqueta={e} />
      ))}
      {opcao.detalhe ? (
        <span className="txt-linha2 min-w-24 flex-1 basis-0 truncate text-tinta-secundaria">{opcao.detalhe}</span>
      ) : null}
    </span>
  );
}

/**
 * A miniatura da opção, sempre na MESMA caixa (32 px na lista, 24 no campo):
 * a foto em recorte quadrado, raio 8 (p.14); sem foto, o ícone no bloco do tom
 * neutro; a pessoa, as iniciais no avatar redondo (p.24). A foto que falha
 * (URL assinada vencida) cai no ícone, como a miniatura da tabela da frota.
 * `alt` vazio: o nome está escrito ao lado.
 */
function Visual({ opcao, tamanho }: { readonly opcao: OpcaoDeSelecao; readonly tamanho: 'p' | 'm' }) {
  const [falhou, setFalhou] = useState<string | null>(null);
  const caixa = tamanho === 'm' ? 'size-8' : 'size-6';
  const Icone = opcao.icone;

  if (opcao.imagem !== undefined && opcao.imagem !== falhou) {
    const url = opcao.imagem;
    return (
      <img
        src={url}
        alt=""
        width={tamanho === 'm' ? 32 : 24}
        height={tamanho === 'm' ? 32 : 24}
        loading="lazy"
        decoding="async"
        className={`${caixa} shrink-0 rounded-etiqueta bg-rebaixada object-cover`}
        onError={() => {
          setFalhou(url);
        }}
      />
    );
  }
  if (Icone) {
    return (
      <span aria-hidden="true" className={`${caixa} flex shrink-0 items-center justify-center rounded-etiqueta bg-nao-alocado-fundo`}>
        <Icone className="size-[var(--icone-controle)] shrink-0 text-nao-alocado" />
      </span>
    );
  }
  if (opcao.iniciais !== undefined) {
    return (
      <span
        aria-hidden="true"
        className={`${caixa} txt-legenda flex shrink-0 items-center justify-center rounded-pill bg-nao-alocado-fundo font-semibold text-tinta`}
      >
        {opcao.iniciais}
      </span>
    );
  }
  /* A opção sem visual numa lista que tem (a vazia, "Todas"): a caixa fica,
     vazia, para o texto alinhar. */
  return <span aria-hidden="true" className={`${caixa} shrink-0`} />;
}
